import { create } from 'zustand';
import { apiClient } from '../lib/api';

export interface PolicyAllocation { leave_type_id: string; days: number; leave_type_name?: string }
export interface LeavePolicy {
  id: string;
  name: string;
  description?: string | null;
  priority: number;
  designations: string[];
  departments: string[];
  employment_types: string[];
  min_experience_years: number | null;
  max_experience_years: number | null;
  is_active: boolean;
  assigned_count?: number;
  allocations?: PolicyAllocation[];
}
export interface PolicyOptions {
  designations: string[];
  departments: string[];
  employmentTypes: string[];
  leaveTypes: { id: string; name: string; days_allowed: number }[];
}
export interface EmployeeBalance {
  leave_type_id: string; type: string; code?: string; days_allowed?: number;
  allocated: number; taken: number; remaining: number; year: number;
}

interface State {
  policies: LeavePolicy[];
  options: PolicyOptions;
  loading: boolean;
  error: string | null;

  loadPolicies: () => Promise<void>;
  loadOptions: () => Promise<void>;
  getPolicy: (id: string) => Promise<LeavePolicy>;
  createPolicy: (data: any) => Promise<LeavePolicy>;
  updatePolicy: (id: string, data: any) => Promise<LeavePolicy>;
  deletePolicy: (id: string) => Promise<void>;
  getMatches: (id: string) => Promise<any[]>;
  applyPolicy: (id: string, year: number) => Promise<{ employees: number; balancesWritten: number }>;
  assign: (employeeId: string, policyId: string | null) => Promise<void>;

  getEmployeeBalances: (employeeId: string, year: number) => Promise<EmployeeBalance[]>;
  setBalance: (employeeId: string, leaveTypeId: string, allocated: number, year: number) => Promise<EmployeeBalance>;
}

const unwrap = (res: any) => res.data?.data ?? res.data;

export const useLeavePolicyStore = create<State>((set) => ({
  policies: [],
  options: { designations: [], departments: [], employmentTypes: [], leaveTypes: [] },
  loading: false,
  error: null,

  loadPolicies: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get('/people/leave-policies');
      set({ policies: unwrap(res) ?? [], loading: false });
    } catch (e: any) {
      set({ loading: false, error: e?.response?.data?.message || 'Failed to load policies' });
    }
  },
  loadOptions: async () => {
    try {
      const res = await apiClient.get('/people/leave-policies/options');
      set({ options: unwrap(res) });
    } catch (e: any) {
      set({ error: e?.response?.data?.message || 'Failed to load options' });
    }
  },
  getPolicy: async (id) => unwrap(await apiClient.get(`/people/leave-policies/${id}`)),
  createPolicy: async (data) => {
    const p = unwrap(await apiClient.post('/people/leave-policies', data));
    set((s) => ({ policies: [...s.policies, p] }));
    return p;
  },
  updatePolicy: async (id, data) => {
    const p = unwrap(await apiClient.patch(`/people/leave-policies/${id}`, data));
    set((s) => ({ policies: s.policies.map((x) => (x.id === id ? { ...x, ...p } : x)) }));
    return p;
  },
  deletePolicy: async (id) => {
    await apiClient.delete(`/people/leave-policies/${id}`);
    set((s) => ({ policies: s.policies.filter((x) => x.id !== id) }));
  },
  getMatches: async (id) => unwrap(await apiClient.get(`/people/leave-policies/${id}/matches`)) ?? [],
  applyPolicy: async (id, year) => unwrap(await apiClient.post(`/people/leave-policies/${id}/apply`, { year })),
  assign: async (employeeId, policyId) => {
    await apiClient.post('/people/leave-policies/assign', { employeeId, policyId });
  },

  getEmployeeBalances: async (employeeId, year) =>
    unwrap(await apiClient.get(`/people/leave/balances/employee/${employeeId}?year=${year}`)) ?? [],
  setBalance: async (employeeId, leaveTypeId, allocated, year) =>
    unwrap(await apiClient.put(`/people/leave/balances/employee/${employeeId}`, { leaveTypeId, allocated, year })),
}));
